import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import { Badge, Button, Field, InlineAlert, Input, Section } from '@aeris/ui'

import { ApiError } from '../lib/api.ts'
import {
  fetchMoneddSettings,
  removeMoneddSettings,
  saveMoneddSettings,
} from '../lib/pix-charge.ts'

/**
 * O login da Monedd desta empresa — F204.
 *
 * É daqui que sai o Pix do atendimento. O servidor testa o login antes de
 * guardar e nunca devolve a senha: a tela só sabe se está configurada e com
 * qual e-mail.
 */
export const MoneddSettings = (): ReactNode => {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [editing, setEditing] = useState(false)

  const settings = useQuery({
    queryKey: ['monedd-settings'],
    queryFn: async ({ signal }) => await fetchMoneddSettings(signal),
  })

  const save = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async () =>
      await saveMoneddSettings({ email: email.trim(), password }),
    onSuccess: (next) => {
      queryClient.setQueryData(['monedd-settings'], next)
      setPassword('')
      setEditing(false)
    },
  })

  const remove = useMutation({
    mutationFn: removeMoneddSettings,
    onSuccess: (next) => {
      queryClient.setQueryData(['monedd-settings'], next)
    },
  })

  const configured = settings.data?.configured === true
  const showForm = !configured || editing
  const failure =
    save.error instanceof ApiError
      ? save.error.message
      : save.isError
        ? 'Não foi possível salvar agora.'
        : null

  return (
    <Section
      title="Pix pelo atendimento (Monedd)"
      description="A conta da Monedd de onde sai o Pix que o vendedor gera no chat."
    >
      <div className="flex flex-col gap-3">
        {settings.isPending ? null : configured && !editing ? (
          <div className="flex flex-wrap items-center gap-3">
            <Badge tone="success">Conectada</Badge>
            <span className="text-(length:--text-sm)">
              {settings.data?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEmail(settings.data?.email ?? '')
                setEditing(true)
              }}
            >
              Trocar login
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate()
              }}
            >
              Desligar
            </Button>
          </div>
        ) : null}

        {showForm && !settings.isPending ? (
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <Field label="E-mail da Monedd" className="min-w-56 flex-1">
              {(field) => (
                <Input
                  {...field}
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                />
              )}
            </Field>
            <Field label="Senha da Monedd" className="min-w-56 flex-1">
              {(field) => (
                <Input
                  {...field}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                />
              )}
            </Field>
            <div className="flex gap-2">
              {editing ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditing(false)
                  }}
                >
                  Voltar
                </Button>
              ) : null}
              <Button
                type="submit"
                variant="primary"
                loading={save.isPending}
                disabled={email.trim() === '' || password === ''}
              >
                Testar e salvar
              </Button>
            </div>
          </form>
        ) : null}

        {failure === null ? null : (
          <InlineAlert tone="danger">{failure}</InlineAlert>
        )}
      </div>
    </Section>
  )
}
